import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, tap, map, of } from 'rxjs';
import {
    ApplicationStatus,
    ConfigServiceProxy,
    EraApplicantSponsorshipDto,
    EraApplicantSponsorshipServiceProxy,
    EraDto,
    EraNewEmployeeDto,
    EraNewEmployeeServiceProxy,
    EraServiceProxy,
    EraStatus,
    GetEraApplicationsOutput,
    GetReputationCheckDto,
    ReputationCheckServiceProxy,
    SponsorshipApplicationSummaryDto,
} from '@shared/service-proxies/service-proxies';
import { DateTime } from 'luxon';
import { ApplicationService } from '../application/application.service';

@Injectable({
    providedIn: 'root',
})
export class EraService {
    era: EraDto;

    // Default value used until the configurable setting has been loaded from the server
    reputationCheckDateOfTarget = DateTime.fromISO('2027-01-01');

    private erasForSponsorshipSubject = new ReplaySubject<SponsorshipApplicationSummaryDto[]>(1);
    readonly erasForSponsorship$ = this.erasForSponsorshipSubject.asObservable();

    constructor(
        private readonly applicationService: ApplicationService,
        private readonly eraServiceProxy: EraServiceProxy,
        private readonly eraApplicantSponsorshipServiceProxy: EraApplicantSponsorshipServiceProxy,
        private readonly eraNewEmployeeServiceProxy: EraNewEmployeeServiceProxy,
        private readonly configServiceProxy: ConfigServiceProxy,
        private readonly reputationCheckServiceProxy: ReputationCheckServiceProxy
    ) {
        this.configServiceProxy.getSironaSettings().subscribe((settings) => {
            if (settings.reputationCheckDateOfTarget) {
                this.reputationCheckDateOfTarget = settings.reputationCheckDateOfTarget;
            }
        });
    }

    public getEra(caseId: string): Observable<EraDto> {
        return this.eraServiceProxy.getEra(caseId).pipe(
            tap((output) => {
                this.era = output.era;
            }),
            map((output) => output.era)
        );
    }

    public closeEra(): void {
        this.era = this.era;
    }

    get caseId(): string {
        return this.era.application.caseId;
    }

    get status(): ApplicationStatus {
        return this.era.application.status;
    }

    public getApplicantSponsorship(): Observable<EraApplicantSponsorshipDto> {
        return this.eraApplicantSponsorshipServiceProxy.get(this.caseId);
    }

    public getNewEmployee(): Observable<EraNewEmployeeDto> {
        return this.eraNewEmployeeServiceProxy.get(this.caseId);
    }

    public createOrUpdateNewEmployee(dto: EraNewEmployeeDto): Observable<EraStatus> {
        this.applicationService.setStepToReview(EraStatus.NewEmployee);
        return this.eraNewEmployeeServiceProxy.createOrUpdate(this.caseId, dto);
    }

    public isAhvAlreadyEmployed(ahv: string): Observable<boolean> {
        return this.eraNewEmployeeServiceProxy.isAhvAlreadyEmployed(this.caseId, ahv);
    }

    public getReputationCheck(): Observable<GetReputationCheckDto> {
        return this.reputationCheckServiceProxy.getReputationCheckForApplication(this.era.application.id);
    }

    public setHasEntriesForPersonCheck(personCheckId: number, hasEntries: boolean): Observable<void> {
        return this.reputationCheckServiceProxy.setHasEntriesForPersonCheck(personCheckId, hasEntries);
    }

    public releaseEra(comment: string): Observable<void> {
        return this.eraServiceProxy.releaseEra(this.caseId, comment);
    }

    loadErasForSponsorship(uid: string): Observable<SponsorshipApplicationSummaryDto[]> {
        if (!uid) {
            return of([]);
        }

        return this.eraServiceProxy.getErasForSponsorship(uid).pipe(tap((eras) => this.erasForSponsorshipSubject.next(eras)));
    }

    getMyEras(): Observable<SponsorshipApplicationSummaryDto[]> {
        return this.eraServiceProxy.getMyEras();
    }

    getApplications(filter: string, sorting: string, maxResultCount: number, skipCount: number): Observable<GetEraApplicationsOutput> {
        return this.eraServiceProxy.getEras(filter, sorting, maxResultCount, skipCount);
    }

    getStepUrl(nextStep: EraStatus): string {
        return this.applicationService.getNextStep(EraService.getUrlForStep(nextStep));
    }

    getNextStatus(status: EraStatus): Observable<EraStatus> {
        const isAuthority = abp.auth.isGranted('Pages.Authority.Applications');
        return this.eraServiceProxy.getNextStatus(this.caseId, status).pipe(
            map((nextStatus) => {
                // Authority goes to 'finish review' and not to 'release'
                if (isAuthority && nextStatus === EraStatus.Release) {
                    return EraStatus.FinishReview;
                }
                return nextStatus;
            })
        );
    }

    getEvaluationComments(): Observable<string> {
        return this.eraServiceProxy.getEvaluationComments(this.caseId);
    }

    static getUrlForStep(step: EraStatus): string {
        switch (step) {
            case EraStatus.ApplicantSponsorship:
                return 'applicant-sponsorship';
            case EraStatus.NewEmployee:
                return 'new-employee';
            case EraStatus.Release:
                return 'release';
            case EraStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }
}
